<template>
  <div class="payment-page">
    <div class="toolbar">
      <el-button type="primary" @click="openDialog">新增支付通道</el-button>
    </div>
    <el-table :data="list" stripe style="width:100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="通道名称" />
      <el-table-column prop="payment" label="支付方式" width="120">
        <template #default="{row}">
          <el-tag v-if="row.payment === 'alipay'" type="primary">支付宝</el-tag>
          <el-tag v-else-if="row.payment === 'wechat'" type="success">微信</el-tag>
          <el-tag v-else>{{ row.payment }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="uuid" label="UUID" width="280" show-overflow-tooltip />
      <el-table-column label="手续费" width="150">
        <template #default="{row}">
          <div v-if="row.handling_fee_fixed || row.handling_fee_percent">
            <span v-if="row.handling_fee_fixed">固定：{{ (row.handling_fee_fixed / 100).toFixed(2) }}元</span>
            <span v-if="row.handling_fee_percent">比例：{{ row.handling_fee_percent }}%</span>
          </div>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column prop="enable" label="状态" width="100">
        <template #default="{row}">
          <el-tag :type="row.enable ? 'success' : 'danger'">
            {{ row.enable ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="80" />
      <el-table-column label="操作" width="200">
        <template #default="{row}">
          <el-button size="small" @click="edit(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="dialogVisible" :title="form.id ? '编辑支付通道' : '新增支付通道'" width="700px">
      <el-form :model="form" label-width="140px">
        <el-form-item label="通道名称">
          <el-input v-model="form.name" placeholder="请输入通道名称" />
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select v-model="form.payment" placeholder="请选择支付方式">
            <el-option label="支付宝" value="alipay" />
            <el-option label="微信" value="wechat" />
          </el-select>
        </el-form-item>
        <el-form-item label="UUID">
          <el-input v-model="form.uuid" placeholder="32位UUID，用于回调路由" :disabled="!!form.id" />
          <el-button v-if="!form.id" size="small" @click="generateUUID" style="margin-left:10px">生成</el-button>
        </el-form-item>
        <el-form-item label="回调域名">
          <el-input v-model="form.notify_domain" placeholder="例如：https://your-domain.com" />
        </el-form-item>
        <el-form-item label="是否启用">
          <el-switch v-model="form.enable" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" />
        </el-form-item>
        <el-form-item label="固定手续费（元）">
          <el-input-number v-model="form.handling_fee_fixed_yuan" :precision="2" :min="0" :step="0.01" />
          <span class="tips">输入元，会自动转换为分</span>
        </el-form-item>
        <el-form-item label="手续费比例（%）">
          <el-input-number v-model="form.handling_fee_percent" :precision="2" :min="0" :max="100" :step="0.01" />
          <span class="tips">例如：0.5 表示 0.5%</span>
        </el-form-item>
        <el-form-item label="图标URL">
          <el-input v-model="form.icon" placeholder="图标URL（可选）" />
        </el-form-item>
        <el-form-item label="支付配置（JSON）">
          <el-input v-model="form.config_json" type="textarea" :rows="8" placeholder='{"app_id":"...","private_key":"...","public_key":"..."}' />
          <div class="tips">支付宝配置示例：{"app_id":"2021002118654108","private_key":"...","public_key":"..."}</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible=false">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '../../utils/request'
import store from '../../store'

const list = ref([])
const dialogVisible = ref(false)
const loading = ref(false)
const form = ref({
  id: null,
  name: '',
  payment: 'alipay',
  uuid: '',
  notify_domain: '',
  enable: 1,
  sort: 0,
  handling_fee_fixed_yuan: null,
  handling_fee_percent: null,
  icon: '',
  config_json: ''
})

const generateUUID = () => {
  // 生成32位UUID
  const chars = '0123456789abcdef'
  let uuid = ''
  for (let i = 0; i < 32; i++) {
    uuid += chars[Math.floor(Math.random() * chars.length)]
  }
  form.value.uuid = uuid
}

const loadList = async () => {
  try {
    const data = await request.get('/v1/admin/payment/list')
    list.value = (data.list || []).map(item => ({
      ...item,
      enable: Number(item.enable) || 0,
      sort: Number(item.sort) || 0
    }))
  } catch (e) {
    ElMessage.error(e.toString())
  }
}

const openDialog = () => {
  form.value = {
    id: null,
    name: '',
    payment: 'alipay',
    uuid: '', // 新建时不传，后端会自动生成
    notify_domain: '',
    enable: 1,
    sort: 0,
    handling_fee_fixed_yuan: null,
    handling_fee_percent: null,
    icon: '',
    config_json: '{}'
  }
  dialogVisible.value = true
}

const edit = (row) => {
  let config = {}
  try {
    config = typeof row.config === 'string' ? JSON.parse(row.config) : (row.config || {})
  } catch (e) {
    console.error('解析配置失败', e)
  }

  form.value = {
    id: row.id,
    name: row.name,
    payment: row.payment,
    uuid: row.uuid,
    notify_domain: row.notify_domain || '',
    enable: Number(row.enable) || 0,
    sort: Number(row.sort) || 0,
    handling_fee_fixed_yuan: row.handling_fee_fixed ? (row.handling_fee_fixed / 100) : null,
    handling_fee_percent: row.handling_fee_percent || null,
    icon: row.icon || '',
    config_json: JSON.stringify(config, null, 2)
  }
  dialogVisible.value = true
}

const submit = async () => {
  if (!form.value.name) {
    ElMessage.warning('请输入通道名称')
    return
  }
  if (!form.value.payment) {
    ElMessage.warning('请选择支付方式')
    return
  }
  // 新建时不需要 UUID（后端自动生成），更新时需要验证 UUID 格式
  if (form.value.id && form.value.uuid && form.value.uuid.length !== 32) {
    ElMessage.warning('UUID必须为32位字符')
    return
  }

  // 验证JSON配置
  let configObj = {}
  try {
    if (form.value.config_json) {
      configObj = JSON.parse(form.value.config_json)
    }
  } catch (e) {
    ElMessage.error('配置JSON格式错误：' + e.message)
    return
  }

  loading.value = true
  try {
    const payload = {
      id: form.value.id,
      name: form.value.name,
      payment: form.value.payment,
      uuid: form.value.uuid || undefined, // 新建时后端会自动生成，更新时不传
      notify_domain: form.value.notify_domain || null,
      enable: form.value.enable,
      sort: form.value.sort || 0,
      handling_fee_fixed_yuan: form.value.handling_fee_fixed_yuan || null,
      handling_fee_percent: form.value.handling_fee_percent || null,
      icon: form.value.icon || null,
      config_array: configObj // 使用 config_array，后端会转换为 JSON
    }
    
    // 新建时如果未提供 UUID，不传 uuid 字段，让后端自动生成
    if (!form.value.id && !form.value.uuid) {
      delete payload.uuid
    }

    if (form.value.id) {
      await request.post('/v1/admin/payment/update', payload)
    } else {
      await request.post('/v1/admin/payment/create', payload)
    }
    ElMessage.success('操作成功')
    dialogVisible.value = false
    loadList()
  } catch (e) {
    console.error('保存支付通道失败:', e)
    ElMessage.error(e.toString())
  } finally {
    loading.value = false
  }
}

const remove = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除这个支付通道吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await request.post('/v1/admin/payment/delete', { id: row.id })
    ElMessage.success('删除成功')
    loadList()
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.toString())
    }
  }
}

onMounted(() => {
  if (store.state.token) {
    loadList()
  }
})
</script>

<style scoped>
.payment-page .toolbar {
  margin-bottom: 15px;
}
.tips {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
</style>

